import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ScheduleDialog from "@/components/ScheduleDialog";
import { Share2, Loader2, Coins, Copy, Wand2, Instagram, Youtube, Facebook, Linkedin, Music2, CalendarClock } from "lucide-react";

const PLATFORMS = [
  { id: "instagram",     label: "Instagram",     icon: Instagram },
  { id: "tiktok",        label: "TikTok",        icon: Music2 },
  { id: "youtube",       label: "YouTube",       icon: Youtube },
  { id: "youtube_short", label: "YT Shorts",     icon: Youtube },
  { id: "facebook",      label: "Facebook",      icon: Facebook },
  { id: "linkedin",      label: "LinkedIn",      icon: Linkedin },
  { id: "twitter",       label: "X / Twitter",   icon: Share2 },
];

export default function AISocial() {
  const { isOwner, refreshBalance } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("engaging");
  const [language, setLanguage] = useState("en");
  const [niche, setNiche] = useState("general");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  useEffect(() => { api.get("/ai/pricing").then((r) => setPricing(r.data)); }, []);

  const run = async () => {
    if (!topic.trim()) return toast.error("Beschrijf waarover je wil posten");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/social-post", { topic, platform, tone, language, niche });
      setResult(data);
      toast.success("Post gegenereerd");
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Genereren mislukt");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label = "Gekopieerd") => {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const copyPost = async () => {
    if (!result) return;
    const tags = Array.isArray(result.hashtags) ? result.hashtags.map((h) => `#${h}`).join(" ") : "";
    const full = [result.main || "", "", tags].filter(Boolean).join("\n");
    await navigator.clipboard.writeText(full);
    toast.success("Post + hashtags gekopieerd");
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]" data-testid="ai-social-page">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">SOCIAL · CONTENT · STUDIO</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Posts die <span className="font-bold text-amber-400">viral</span> gaan.
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Genereer platform-native captions met 3 hook-varianten en de juiste hashtags — voor elke niche.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Input */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-[#121214] border border-white/5 rounded-lg p-5 space-y-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">PLATFORM</Label>
              <div className="grid grid-cols-4 gap-2">
                {PLATFORMS.map((p) => {
                  const Icon = p.icon;
                  const active = platform === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setPlatform(p.id)}
                      data-testid={`platform-${p.id}`}
                      className={`flex flex-col items-center gap-1 rounded-md border py-2.5 transition-colors ${
                        active
                          ? "border-amber-500 bg-amber-500/10"
                          : "border-white/10 bg-[#0b0b0d] hover:border-white/25"
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${active ? "text-amber-400" : "text-zinc-400"}`} strokeWidth={1.5} />
                      <span className={`text-[9px] font-mono-tech ${active ? "text-amber-400" : "text-zinc-500"}`}>
                        {p.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">ONDERWERP / BOODSCHAP *</Label>
              <Textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Bijvoorbeeld: 'Lancering nieuwe koffiebonen uit Ethiopië'"
                rows={4}
                data-testid="social-topic"
                className="mt-2 bg-[#0b0b0d] border-white/10"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">NICHE</Label>
                <Input
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  placeholder="fashion, tech…"
                  data-testid="social-niche"
                  className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
                />
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TOON</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="social-tone" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="engaging">Engaging</SelectItem>
                    <SelectItem value="funny">Grappig</SelectItem>
                    <SelectItem value="professional">Professioneel</SelectItem>
                    <SelectItem value="luxury">Luxe</SelectItem>
                    <SelectItem value="edgy">Edgy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TAAL</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="social-lang" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Button
            onClick={run}
            disabled={busy || !topic.trim()}
            data-testid="social-generate"
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Genereer post
            {!isOwner && pricing && (
              <span className="ml-2 text-[10px] font-mono-tech opacity-75 flex items-center gap-1">
                <Coins className="w-3 h-3" /> {pricing.social_post_credits}
              </span>
            )}
          </Button>
        </div>

        {/* Output */}
        <div className="lg:col-span-7">
          {result ? (
            <div className="space-y-4" data-testid="social-result">
              <div className="bg-[#121214] border border-white/5 rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between mb-1">
                  <Label className="font-mono-tech text-[10px] text-zinc-500">POST · {platform.toUpperCase()}</Label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setScheduleOpen(true)}
                      data-testid="social-schedule"
                      className="text-xs font-mono-tech text-amber-400 hover:text-amber-300 flex items-center gap-1.5"
                    >
                      <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} /> Plan in kalender
                    </button>
                    <button
                      onClick={copyPost}
                      data-testid="social-copy-post"
                      className="text-xs font-mono-tech text-amber-400 hover:text-amber-300 flex items-center gap-1.5"
                    >
                      <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Kopieer post
                    </button>
                  </div>
                </div>
                <p className="text-zinc-100 leading-relaxed whitespace-pre-wrap">{result.main}</p>
                {result.cta && (
                  <div className="text-sm text-amber-400 font-medium">→ {result.cta}</div>
                )}
                {Array.isArray(result.hashtags) && result.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {result.hashtags.map((h, i) => (
                      <span key={i} className="text-xs text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-full">
                        #{h}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {Array.isArray(result.hooks) && result.hooks.length > 0 && (
                <div className="bg-[#121214] border border-white/5 rounded-lg p-6">
                  <Label className="font-mono-tech text-[10px] text-zinc-500 mb-3 block">ALTERNATIEVE HOOKS</Label>
                  <ul className="space-y-3">
                    {result.hooks.map((h, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 text-sm text-zinc-200">
                        <span className="flex-1">
                          <span className="font-mono-tech text-[9px] text-amber-400 mr-2">#{i + 1}</span>
                          {h}
                        </span>
                        <button
                          onClick={() => copy(h, "Hook gekopieerd")}
                          className="text-zinc-500 hover:text-amber-400 flex-shrink-0"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-white/10 rounded-lg p-12 text-center">
              <Share2 className="w-10 h-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
              <div className="text-sm text-zinc-400">
                Kies een platform, beschrijf je onderwerp — en krijg een klaar-om-te-plaatsen post.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule dialog */}
      <ScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        content={result ? {
          title: "",
          body: [result.main, "", (result.hashtags || []).map((h) => `#${h}`).join(" ")].filter(Boolean).join("\n"),
          hashtags: result.hashtags || [],
          platform,
          sourceLanguage: language,
          contentType: "social",
        } : null}
      />
    </div>
  );
}
